"use client";
import { evaluate, toast } from "@kyan/ui";
import { useDebounceValue } from "usehooks-ts";

import { defaultUnderlying } from "#/config/markets";
import { useTradeButtonFeatureFlag } from "#/hooks/featureFlags/useTradeButtonFeatureFlag";
import { useHedgeSummaryController } from "#/hooks/useHedgeSummary.controller";
import { usePortfolioToastAction } from "#/hooks/usePortfolioToastActions";
import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { useValidateOptionSize } from "#/hooks/useValidateOptionSize";
import { useValidatePerpSize } from "#/hooks/useValidatePerpSize";
import { useCalculateUserRisk } from "#/services/kyan/hooks/useCalculateUserRisk";
import { usePostMarketOrder } from "#/services/kyan/hooks/usePostMarketOrder";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useEquityModalTab } from "#/store/app/equityModal/equityModalTab";
import { useShowEquityModal } from "#/store/app/equityModal/showEquityModal";
import { useMarket } from "#/store/app/portfolio/market";
import { useShowTradeConfirmation } from "#/store/app/portfolio/settings/showTradeConfirmation";
import { usePositionsDrawerMarket } from "#/store/app/positionsDrawer/positionsDrawerMarket";
import { usePositionsDrawerSettings } from "#/store/app/positionsDrawer/positionsDrawerSettings";
import { usePositionsDrawerTab } from "#/store/app/positionsDrawer/positionsDrawerTab";
import { useTradePanelTab } from "#/store/app/tradePanel/tradePanelTab";
import { useIsOptionHedged } from "#/store/options/tradePanel/isOptionHedged";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionLimitPrice } from "#/store/options/tradePanel/optionLimitPrice";
import { useOptionMarketOrderType } from "#/store/options/tradePanel/optionMarketOrderType";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpMarketOrderType } from "#/store/perps/tradePanel/perpMarketOrderType";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { useAccount } from "#/store/wallet/account/account";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type PostOptionLegMarket, type PostPerpLegMarket } from "#/types/api";
import { type Trade } from "#/types/orders";
import { getMmrImr } from "#/utils/getMmrImr";
import {
  createPerpInstrumentName,
  formatInstrumentName,
} from "#/utils/instrument.utils";
import {
  isBuy,
  toDirection,
  toTradeDirection,
} from "#/utils/marketActionConverter";
import { getMarketByUnderlying } from "#/utils/marketPair";
import { capitalize } from "#/utils/string.utils";

import { useValidateOptionLimitPrice } from "../../../OptionsTab/components/SelectedOption/hooks/useValidateOptionLimitPrice";
import { useValidatePerpLimitPrice } from "../../../PerpsTab/hooks/useValidatePerpLimitPrice";

const useOptionsDetails = () => {
  const { option } = useOption();
  const { optionLimitPrice } = useOptionLimitPrice();
  const { optionSize } = useOptionSize();
  const { optionMarketOrderType } = useOptionMarketOrderType();
  const { account } = useAccount();

  // Placeholder logic before Options Updater:
  const [debouncedSize] = useDebounceValue(optionSize, 500);
  const [debouncedLimitPrice] = useDebounceValue(optionLimitPrice, 500);

  const { limitPriceError } = useValidateOptionLimitPrice({
    option,
    limitPrice: optionLimitPrice,
  });
  const sizeError = useValidateOptionSize({
    size: debouncedSize,
    underlying: option?.underlying ?? defaultUnderlying,
  });

  const isMissingDetails =
    !debouncedSize ||
    !debouncedLimitPrice ||
    Boolean(limitPriceError) ||
    Boolean(sizeError);

  const marketOrder: PostOptionLegMarket = {
    instrument_name: option?.instrument_name ?? "",
    direction: option?.direction || "buy",
    contracts: Number(debouncedSize),
  };

  const trade: Trade = {
    market_order: marketOrder,
    limit_price: Number(debouncedLimitPrice),
    type: optionMarketOrderType,
    taker: account!,
  };

  const optionMarket = getMarketByUnderlying(
    option?.underlying || defaultUnderlying,
  );
  const isValidForRiskCalc = Boolean(account && !sizeError);
  const isValid = Boolean(account && !limitPriceError && !sizeError);

  return {
    market: optionMarket,
    isValid,
    isMissingDetails,
    trade,
    size: debouncedSize,
    limitPrice: debouncedLimitPrice,
    direction: option?.direction,
    limitPriceError,
    sizeError,
    isValidForRiskCalc,
  };
};

const usePerpsDetails = () => {
  const { market } = useMarket();
  const { perpSize } = usePerpSize();
  const { perpLimitPrice } = usePerpLimitPrice();
  const { perpMarketOrderType } = usePerpMarketOrderType();
  const { perpIsBuy } = usePerpIsBuy();
  const { account } = useAccount();

  const [debouncedAmount] = useDebounceValue(perpSize, 500);
  const [debouncedPerpLimitPrice] = useDebounceValue(perpLimitPrice, 500);

  const { limitPriceError } = useValidatePerpLimitPrice();
  const { sizeError, hasSizeError, hasSizeWarning } = useValidatePerpSize();

  const isMissingDetails =
    !debouncedAmount ||
    !debouncedPerpLimitPrice ||
    Boolean(limitPriceError) ||
    hasSizeError ||
    hasSizeWarning;

  const marketOrder: PostPerpLegMarket = {
    instrument_name: `${market.pair}-PERPETUAL`,
    direction: toDirection(perpIsBuy),
    contracts: Number(debouncedAmount),
  };

  const trade: Trade = {
    market_order: marketOrder,
    limit_price: Number(debouncedPerpLimitPrice),
    type: perpMarketOrderType,
    taker: account!,
  };

  const isValidForRiskCalc =
    Boolean(account) && !hasSizeError && !hasSizeWarning;
  const isValid =
    Boolean(account && !limitPriceError) && !hasSizeError && !hasSizeWarning;

  return {
    market,
    isValid,
    isMissingDetails,
    trade,
    size: debouncedAmount,
    limitPrice: debouncedPerpLimitPrice,
    direction: toDirection(perpIsBuy),
    limitPriceError,
    sizeError,
    isValidForRiskCalc,
  };
};

export const useMarketTradeButtonController = (onSuccess?: () => void) => {
  const { isPerps } = useTradePanelTab();
  const optionDetails = useOptionsDetails();
  const perpDetails = usePerpsDetails();
  const { setOptionSize } = useOptionSize();
  const { setPerpSizeInputValue } = usePerpSize();
  const { setPositionsDrawerTab } = usePositionsDrawerTab();
  const { showTradeConfirmation } = useShowTradeConfirmation();
  const { setPositionsDrawerSettings } = usePositionsDrawerSettings();
  const { isOptionHedged } = useIsOptionHedged();
  const { hedgeDetails } = useHedgeSummaryController();
  const { setMarket: setDrawerMarket } = usePositionsDrawerMarket();
  const { isDevMode } = useIsDevMode();
  const { setShowEquityModal } = useShowEquityModal();
  const { setEquityModalTab } = useEquityModalTab();
  const { setEquityModalPortfolio } = useEquityModalPortfolio();
  const { refetch } = useRefetchAccountStateIfStale();
  const { getAction } = usePortfolioToastAction();
  const { isTradeButtonEnabled, tradeButtonTitle } =
    useTradeButtonFeatureFlag();

  const {
    market,
    isValid,
    isMissingDetails,
    trade,
    size,
    limitPriceError,
    direction: tradeDirection,
    sizeError,
    isValidForRiskCalc,
  } = isPerps ? perpDetails : optionDetails;

  const shouldCalculateHedge = !isPerps && isOptionHedged && hedgeDetails;

  const { portfolioState } = useAccountState(market.underlying);
  const { isLiquidated, hasZeroEquity, hasTransferableEquity } =
    usePortfolioValidations(market);

  const {
    im,
    mm,
    isLoading: isLoadingRisk,
  } = useCalculateUserRisk({
    inputs: [
      {
        instrument: trade.market_order.instrument_name,
        size,
        isBuy: isBuy(tradeDirection!),
        isEnabled: isValidForRiskCalc,
      },
      ...(shouldCalculateHedge
        ? [
            {
              instrument: createPerpInstrumentName(market.underlying),
              isBuy: hedgeDetails.hedgeIsBuy,
              size: hedgeDetails.hedgeAmount.toString(),
              isEnabled: isValid,
            },
          ]
        : []),
    ],
    option: isPerps ? null : undefined,
  });

  const { imr } = getMmrImr({ equity: portfolioState.equity, mm, im });

  const hasValidImr = imr < 1;

  const label = evaluate({
    [tradeButtonTitle]: !isTradeButtonEnabled,
    "Liquidation Active": isLiquidated,
    "Invalid price": limitPriceError,
    "Invalid size": sizeError,
    "Enter missing details": isMissingDetails,
    "Insufficient equity": hasZeroEquity,
    "Insufficient Margin": !hasValidImr,
    Trade: isValid,
  });

  const { mutate, isPending } = usePostMarketOrder({
    onSuccess: (response) => {
      const direction = capitalize(toTradeDirection(response.direction));
      toast.success({
        title: "Trade successful",
        description: `${direction} ${formatInstrumentName(response.instrument_name)}`,
        action: getAction({ target: "index", label: "View in Portfolio" }),
      });
      cleanOrderValues();

      setPositionsDrawerSettings({ show: true });
      setPositionsDrawerTab("positions");
      setDrawerMarket(market);

      onSuccess?.();

      refetch?.();
    },
    onError: (error: Error) => {
      toast.error({
        title: "Error with market order",
        description: error.message,
      });
    },
  });

  const cleanOrderValues = () => {
    if (isPerps) {
      setPerpSizeInputValue("");
      return;
    }

    setOptionSize("");
  };

  const buttonLabel = `${label}${isDevMode ? " (Dev)" : ""}`;

  const handleTrade = () => {
    if (!isDevMode && !isValid) return;

    mutate(trade);
  };

  const isDisabled =
    !isTradeButtonEnabled ||
    (!isDevMode &&
      (isMissingDetails || hasZeroEquity || !hasValidImr || isLiquidated));
  const isLoading = isPending || isLoadingRisk;

  const handleSecondaryButtonClick = () => {
    setShowEquityModal(true);
    setEquityModalPortfolio(market.underlying);
    setEquityModalTab(hasTransferableEquity ? "transfer" : "deposit");
  };

  const secondaryButtonLabel = hasTransferableEquity
    ? "Transfer/deposit more USDC"
    : "Deposit more USDC";

  const isSecondaryButtonVisible = hasZeroEquity || !hasValidImr;

  return {
    isPerps,
    isLoading,
    isDisabled,
    buttonLabel,
    hasValidImr,
    isSecondaryButtonVisible,
    secondaryButtonLabel,
    showTradeConfirmation,
    handleSecondaryButtonClick,
    handleTrade,
  };
};
